import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {useFragment} from 'relay-hooks';
import {useMutation} from 'react-relay-mutation';
import {ShotDetailModal} from './ShotDetailModal';
import graphql from 'babel-plugin-relay/macro';
import {TourShotsGrid_tour$key} from '../../../__generated__/TourShotsGrid_tour.graphql';
import {TourShotsGridGenerateMutation} from '../../../__generated__/TourShotsGridGenerateMutation.graphql';
import {TourShotsGridRegenerateMutation} from '../../../__generated__/TourShotsGridRegenerateMutation.graphql';
import {TourShotsGridGenerateStagedMutation} from '../../../__generated__/TourShotsGridGenerateStagedMutation.graphql';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';

const tourShotsGridFragment = graphql`
  fragment TourShotsGrid_tour on Tour {
    id
    title
    status {
      status
    }
    videoURL
    thumbnailURL
    shots {
      id
      shotType
      shotStatus
      roomType
      frameScore
      sourceClipIndex
      sourceFramePosition
      originalImageURI
      enhancedImageURI
      stagedImageURI
      createdAt
      errorMessage
    }
  }
`;

const generateMutation = graphql`
  mutation TourShotsGridGenerateMutation($tourId: ID!) {
    generateTourShots(input: {tourId: $tourId}) {
      tour {
        id
        shots {
          id
        }
      }
    }
  }
`;

const regenerateMutation = graphql`
  mutation TourShotsGridRegenerateMutation($shotId: ID!) {
    regenerateShot(input: {shotId: $shotId}) {
      shot {
        id
        shotStatus
        enhancedImageURI
        errorMessage
      }
    }
  }
`;

const generateStagedMutation = graphql`
  mutation TourShotsGridGenerateStagedMutation(
    $shotId: ID!
    $stagingPrompt: String!
  ) {
    generateStagedShot(
      input: {shotId: $shotId, stagingPrompt: $stagingPrompt}
    ) {
      shot {
        id
        shotStatus
        stagedImageURI
        errorMessage
      }
    }
  }
`;

interface Shot {
  id: string;
  shotType: string;
  shotStatus: string;
  roomType: string;
  frameScore: number;
  sourceClipIndex: number;
  sourceFramePosition: string;
  originalImageURI: string;
  enhancedImageURI: string | null;
  stagedImageURI: string | null;
  createdAt: string;
  errorMessage: string | null;
}

interface GroupedShot {
  roomType: string;
  shots: Shot[];
}

interface TourShotsGridProps {
  tourRef: TourShotsGrid_tour$key;
}

export function TourShotsGrid({tourRef}: TourShotsGridProps) {
  const tour = useFragment(tourShotsGridFragment, tourRef);
  const [generateShots] = useMutation<TourShotsGridGenerateMutation>(
    generateMutation
  );
  const [regenerateShot] = useMutation<TourShotsGridRegenerateMutation>(
    regenerateMutation
  );
  const [generateStagedShot] = useMutation<TourShotsGridGenerateStagedMutation>(
    generateStagedMutation
  );
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [stagingPrompts, setStagingPrompts] = React.useState<{
    [key: string]: string;
  }>({});
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [selectedShot, setSelectedShot] = React.useState<Shot | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  // Force re-render to trigger fragment updates
  const refetchTour = React.useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Group shots by room type
  const groupedShots = React.useMemo(() => {
    const groups: {[key: string]: Shot[]} = {};
    tour.shots?.forEach((shot) => {
      const roomType = shot.roomType || 'Unknown';
      if (!groups[roomType]) {
        groups[roomType] = [];
      }
      groups[roomType].push(shot);
    });

    return Object.entries(groups)
      .map(([roomType, shots]) => ({
        roomType,
        shots: shots.sort((a, b) => a.sourceClipIndex - b.sourceClipIndex),
      }))
      .sort((a, b) => a.roomType.localeCompare(b.roomType));
  }, [tour.shots]);

  const processingCount =
    tour.shots?.filter((s) => s.shotStatus === 'PROCESSING').length || 0;

  const handleGenerateShots = () => {
    setIsGenerating(true);
    generateShots({
      variables: {tourId: tour.id},
      onCompleted: (response) => {
        if (response.generateTourShots.tour) {
          setTimeout(() => refetchTour(), 2000);
        }
        setIsGenerating(false);
      },
      onError: () => setIsGenerating(false),
    });
  };

  const handleRegenerateShot = (shotId: string) => {
    regenerateShot({
      variables: {shotId},
      onCompleted: () => {
        setTimeout(() => refetchTour(), 1000);
      },
    });
  };

  const handleGenerateStaged = (shotId: string) => {
    const stagingPrompt =
      stagingPrompts[shotId] || 'modern staging with furniture';
    generateStagedShot({
      variables: {shotId, stagingPrompt},
      onCompleted: () => {
        setTimeout(() => refetchTour(), 1000);
      },
    });
  };

  const downloadImage = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShotClick = (shot: Shot) => {
    setSelectedShot(shot);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedShot(null);
  };

  // Auto-refresh if shots are processing
  React.useEffect(() => {
    if (processingCount > 0) {
      const timer = setTimeout(() => refetchTour(), 5000);
      return () => clearTimeout(timer);
    }
  }, [processingCount, refetchTour]);

  if (tour.status?.status !== 'PROCESSED') {
    return (
      <Box sx={{p: 4, textAlign: 'center'}}>
        <Alert severity="info" sx={{maxWidth: 600, mx: 'auto', mb: 3}}>
          Tour must be fully processed before generating professional photos.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{p: 3}}>
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Professional Photos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            High-quality photos extracted and enhanced from your tour video
          </Typography>
        </Box>

        {tour.shots && tour.shots.length === 0 && (
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerateShots}
            disabled={isGenerating}
            size="large"
          >
            {isGenerating ? 'Generating...' : 'Generate Photos'}
          </Button>
        )}

        {processingCount > 0 && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              Processing {processingCount} photo{processingCount > 1 ? 's' : ''}
              ...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Photos Grid */}
      {tour.shots && tour.shots.length > 0 ? (
        <>
          {groupedShots.map((group) => (
            <Box key={group.roomType} sx={{mb: 6}}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{mb: 3, color: 'primary.main'}}
              >
                {group.roomType}
              </Typography>

              <Grid container spacing={3}>
                {group.shots.map((shot) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={shot.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => handleShotClick(shot)}
                    >
                      {/* Image Display */}
                      <CardMedia
                        component="img"
                        height="200"
                        image={
                          shot.stagedImageURI ||
                          shot.enhancedImageURI ||
                          shot.originalImageURI
                        }
                        alt={`${shot.roomType} - ${shot.sourceFramePosition}`}
                        sx={{objectFit: 'cover'}}
                      />

                      {/* Status and Score */}
                      <CardContent sx={{flexGrow: 1, pb: 1}}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={shot.shotStatus}
                            size="small"
                            color={
                              shot.shotStatus === 'COMPLETED'
                                ? 'success'
                                : shot.shotStatus === 'PROCESSING'
                                ? 'info'
                                : shot.shotStatus === 'FAILED'
                                ? 'error'
                                : 'default'
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            Score: {shot.frameScore}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          Clip {shot.sourceClipIndex} -{' '}
                          {shot.sourceFramePosition}
                        </Typography>

                        {shot.errorMessage && (
                          <Alert severity="error" sx={{mt: 1, py: 0, px: 1}}>
                            <Typography variant="caption">
                              {shot.errorMessage}
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>

                      {/* Actions */}
                      <CardActions
                        sx={{px: 2, pb: 2, flexWrap: 'wrap', gap: 1}}
                      >
                        {/* Enhancement Actions */}
                        {!shot.enhancedImageURI &&
                          shot.shotStatus !== 'PROCESSING' && (
                            <Tooltip title="Enhance image quality">
                              <Button
                                size="small"
                                startIcon={<AutoAwesomeIcon />}
                                onClick={() => handleRegenerateShot(shot.id)}
                              >
                                Enhance
                              </Button>
                            </Tooltip>
                          )}

                        {shot.enhancedImageURI &&
                          shot.shotStatus !== 'PROCESSING' && (
                            <Tooltip title="Re-enhance image">
                              <IconButton
                                size="small"
                                onClick={() => handleRegenerateShot(shot.id)}
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                        {/* Staging Actions */}
                        {shot.enhancedImageURI &&
                          !shot.stagedImageURI &&
                          shot.shotStatus !== 'PROCESSING' && (
                            <Tooltip title="Add virtual staging">
                              <Button
                                size="small"
                                startIcon={<HomeWorkIcon />}
                                onClick={() => handleGenerateStaged(shot.id)}
                              >
                                Stage
                              </Button>
                            </Tooltip>
                          )}

                        {/* Download Actions */}
                        <Box sx={{ml: 'auto', display: 'flex', gap: 0.5}}>
                          <Tooltip title="Download original">
                            <IconButton
                              size="small"
                              onClick={() =>
                                downloadImage(
                                  shot.originalImageURI,
                                  `${shot.roomType}_original_${shot.id}.jpg`
                                )
                              }
                            >
                              <ImageIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {shot.enhancedImageURI && (
                            <Tooltip title="Download enhanced">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  downloadImage(
                                    shot.enhancedImageURI,
                                    `${shot.roomType}_enhanced_${shot.id}.jpg`
                                  )
                                }
                                color="primary"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {shot.stagedImageURI && (
                            <Tooltip title="Download staged">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  downloadImage(
                                    shot.stagedImageURI,
                                    `${shot.roomType}_staged_${shot.id}.jpg`
                                  )
                                }
                                color="secondary"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </>
      ) : (
        <Box sx={{textAlign: 'center', py: 8}}>
          <ImageIcon sx={{fontSize: 80, color: 'text.disabled', mb: 2}} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No professional photos yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            Generate high-quality photos from your tour video
          </Typography>
        </Box>
      )}

      {/* Shot Detail Modal */}
      <ShotDetailModal
        shot={selectedShot}
        open={modalOpen}
        onClose={handleCloseModal}
        onRegenerateShot={handleRegenerateShot}
        onGenerateStaged={handleGenerateStaged}
      />
    </Box>
  );
}
